import { clearAllUI, restoreActiveFlowFromUrl, setUIState, getUIState } from '../utils/clearAll.js';
import { showCookiesPage, showPrivacyPage, showPolicyPage, showLicensesPage, showTransparencyPage, showAboutPage, showSupportPage, showContactPage } from '../info/info-popup.js';
import { getCurrentLanguage, getText } from '../translations/translations.js';
import { createMenuAttributionText } from '../components/attribution.js';
import { createLanguageSwitcher } from '../components/LanguageSelector.js';
import { initializeAreaSelector, getAreaBySlug } from '../area-selector/index.js';
import { parseUrlParams } from '../utils/url-utils.js';
import { IMAGE_PATHS, DOMAIN_NAME, AREA_SELECTOR, LOGGING, APP_VERSION, MENU_SETTINGS } from '/settings.js';

/**
 * Adds version query parameter to URL for cache-busting
 * Matches the logic from static-assets-cache.js to ensure URLs match preloaded assets
 */
function addVersionToUrl(url) {
    if (!APP_VERSION) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}v=${APP_VERSION}`;
}
import { deleteFavoritesForArea, initFavoritesButton } from '../utils/favorites.js';
import { confirmDelete } from '../components/alerts.js';
import { updateMapView } from '../map/map-transitions.js';
import { createIconElement, createAreaIconElement } from '../utils/icon.js';
import { setCardsContext, clearSpotCards } from '../cards/index.js';
import { acquireOverlay, releaseOverlay, addOverlayListener, removeOverlayListener } from '../components/Popup.js';
// Left Menu with self-managed overlay and close button

export async function initializeMenu() {
    // ...existing code...
    const menuToggle = document.getElementById('menu-toggle');
    const menuLeft = document.getElementById('menu-left');
    let isMenuOpen = getUIState('menuOpen');
    let menuOverlay = null;
    let overlayClickHandler = null;
    let closeBtn = null;

    // Add accessibility label to menu toggle button
    if (menuToggle) {
        const menuToggleLabel = await getText('buttons.menuToggle') || 'Open menu';
        menuToggle.setAttribute('aria-label', menuToggleLabel);
        if (!menuToggle.getAttribute('title')) {
            menuToggle.setAttribute('title', menuToggleLabel);
        }
    }

    function log(msg) {
        if (LOGGING.ENABLED && LOGGING.MENU) {
            console.log('[LeftMenu]', msg);
        }
    }

    function createCloseBtn() {
        const btn = document.createElement('button');
        btn.className = 'menu-left-close-btn';
        btn.type = 'button';
        btn.title = 'Close menu';
        btn.innerHTML = '&times;';
        btn.onclick = closeMenu;
        return btn;
    }

    async function createAreaSwitcher() {
        if (!AREA_SELECTOR.ENABLED) return null;

        const areaSwitcher = document.createElement('button');
        areaSwitcher.className = 'menu-btn menu-btn-full menu-area-switcher';
        areaSwitcher.type = 'button';

        // Get current area from URL
        const { areaSlug } = parseUrlParams();
        let currentArea = null;

        if (areaSlug) {
            currentArea = await getAreaBySlug(areaSlug);
        }

        // Get current language for name display
        const currentLang = getCurrentLanguage();
        const nameField = `name_${currentLang}`; // e.g., name_en, name_es, name_de

        // Use unified button classes
        areaSwitcher.classList.add('menu-btn', 'menu-btn-full');

        // Icon (area sprite, fallback to map marker)
        const icon = createAreaIconElement(currentArea?.slug || currentArea?.icon, 'menu-btn-icon', 'map-location-dot-solid');

        // Area name
        const nameSpan = document.createElement('span');
        nameSpan.className = 'menu-btn-text';
        if (currentArea) {
            nameSpan.textContent = currentArea[nameField] || currentArea.name_en || 'Select Area';
        } else {
            nameSpan.textContent = 'Select Area';
        }

        // Arrow indicator
        const arrow = createIconElement('arrows-rotate-solid', 'menu-btn-arrow');

        // Assemble button
        areaSwitcher.appendChild(icon);
        areaSwitcher.appendChild(nameSpan);
        areaSwitcher.appendChild(arrow);

        // Click event
        areaSwitcher.addEventListener('click', () => {
            initializeAreaSelector({
                onAreaSelected: (area) => {
                    if (area) {
                        // Dispatch event that app.js listens to
                        const event = new CustomEvent('areaChanged', { detail: { area } });
                        window.dispatchEvent(event);
                        // Close menu after selection
                        closeMenu();
                    }
                }
            });
        });

        return areaSwitcher;
    }

    async function createFavoritesSection() {
        // Get current area
        const { areaSlug } = parseUrlParams();
        if (!areaSlug) {
            // No area selected, don't show favorites section
            return null;
        }

        const currentArea = await getAreaBySlug(areaSlug);
        if (!currentArea) {
            return null;
        }

        // Container for both buttons
        const container = document.createElement('div');
        container.className = 'menu-btn-section';

        // "See my Favorites" button (80% width) with star icon
        const favoritesBtn = document.createElement('button');
        favoritesBtn.id = 'menu-favorites-btn';
        favoritesBtn.className = 'menu-btn menu-btn-primary';
        favoritesBtn.type = 'button';

        // Star icon
        const starIcon = createIconElement('star-solid-full', 'menu-btn-icon');

        // Text
        const favoritesText = await getText('buttons.showFavorites') || 'Show favorites';
        const textSpan = document.createElement('span');
        textSpan.className = 'menu-btn-text';
        textSpan.textContent = favoritesText;

        favoritesBtn.appendChild(starIcon);
        favoritesBtn.appendChild(textSpan);

        // Trash icon button (20% width)
        const deleteBtn = document.createElement('button');
        deleteBtn.className = 'menu-btn menu-btn-secondary';
        deleteBtn.type = 'button';
        deleteBtn.title = await getText('menu.deleteFavorites') || 'Delete Favorites';
        const trashIcon = createIconElement('trash-solid', 'icon');
        deleteBtn.appendChild(trashIcon);

        // Get current language for delete confirmation
        const currentLang = getCurrentLanguage();

        // Delete button click handler
        deleteBtn.addEventListener('click', async () => {
            // Close menu first so only the popup is visible
            closeMenu();

            const areaName = currentArea[`name_${currentLang}`] || currentArea.name_en || areaSlug;
            const title = await getText('alerts.messages.deleteFavoritesTitle');
            let message = await getText('alerts.messages.deleteFavoritesForArea');
            if (message && message.includes('{area}')) {
                message = message.replace('{area}', areaName);
            } else {
                message = message || `Do you want to delete all favorites for ${areaName}?`;
            }

            await confirmDelete({
                title: title,
                message: message,
                onConfirm: async () => {
                    const deletedCount = deleteFavoritesForArea(currentArea.id);
                    // Clear the map if favorites view is active
                    await updateMapView({ spotsToDisplay: [] });
                    // After deleting, clear any focused marker
                    // Spot selection is now managed only via clearAllUI; do not dispatch 'spotCleared' directly here.
                    // Reset cards banner to the favorites special context (empty state)
                    if (typeof setCardsContext === 'function') {
                        setCardsContext('special', { specialType: 'favorites' }, true);
                        // Ensure any spot context is cleared immediately so the banner shows the favorites empty state
                        try {
                            if (typeof clearSpotCards === 'function') await clearSpotCards(true);
                        } catch (e) {
                            console.warn('Failed to clear spot cards after deleting favorites:', e);
                        }
                    }
                }
            });
        });

        container.appendChild(favoritesBtn);
        container.appendChild(deleteBtn);

        return container;
    }

    async function createInfoButtons() {
        // Check if any info buttons should be shown
        const showAbout = MENU_SETTINGS?.ABOUT !== false;
        const showContact = MENU_SETTINGS?.CONTACT !== false;
        const showSupport = MENU_SETTINGS?.SUPPORT !== false;
        const showQuickGuide = MENU_SETTINGS?.QUICK_GUIDE !== false;

        if (!showAbout && !showSupport && !showContact && !showQuickGuide) return null;

        // Container for info buttons
        const container = document.createElement('div');
        container.className = 'menu-info-buttons';

        // "What is Canary Spots?" button
        if (showAbout) {
            const aboutBtn = document.createElement('button');
            aboutBtn.className = 'menu-btn menu-btn-full';
            aboutBtn.type = 'button';

            // Info icon
            const aboutIcon = createIconElement('circle-info-solid', 'menu-btn-icon');

            // Text
            const aboutText = await getText('menu.aboutUs') || 'What is Canary Spots?';
            const aboutTextSpan = document.createElement('span');
            aboutTextSpan.className = 'menu-btn-text';
            aboutTextSpan.textContent = aboutText;

            aboutBtn.appendChild(aboutIcon);
            aboutBtn.appendChild(aboutTextSpan);

            aboutBtn.addEventListener('click', () => {
                showAboutPage(getCurrentLanguage());
                closeMenu();
            });

            container.appendChild(aboutBtn);
        }

        // "Contact" button
        if (showContact) {
            const contactBtn = document.createElement('button');
            contactBtn.className = 'menu-btn menu-btn-full';
            contactBtn.type = 'button';

            // Envelope icon
            const contactIcon = createIconElement('envelope-solid', 'menu-btn-icon');

            // Text
            const contactText = await getText('menu.contact') || 'Contact';
            const contactTextSpan = document.createElement('span');
            contactTextSpan.className = 'menu-btn-text';
            contactTextSpan.textContent = contactText;

            contactBtn.appendChild(contactIcon);
            contactBtn.appendChild(contactTextSpan);

            contactBtn.addEventListener('click', () => {
                showContactPage(getCurrentLanguage());
                closeMenu();
            });

            container.appendChild(contactBtn);
        }

        // "Buy me a Coffee" button
        if (showSupport) {
            const supportBtn = document.createElement('button');
            supportBtn.className = 'menu-btn menu-btn-full';
            supportBtn.type = 'button';

            // Coffee icon
            const supportIcon = createIconElement('mug-hot-solid-full', 'menu-btn-icon');

            // Text
            const supportText = await getText('menu.supportUs') || 'Buy me a Coffee';
            const supportTextSpan = document.createElement('span');
            supportTextSpan.className = 'menu-btn-text';
            supportTextSpan.textContent = supportText;

            supportBtn.appendChild(supportIcon);
            supportBtn.appendChild(supportTextSpan);

            supportBtn.addEventListener('click', () => {
                showSupportPage(getCurrentLanguage());
                closeMenu();
            });

            container.appendChild(supportBtn);
        }

        // "Show Quick Guide" button
        if (showQuickGuide) {
            const quickGuideBtn = document.createElement('button');
            quickGuideBtn.className = 'menu-btn menu-btn-full';
            quickGuideBtn.type = 'button';

            // Book icon
            const quickGuideIcon = createIconElement('book-solid-full', 'menu-btn-icon');

            // Text
            const quickGuideText = await getText('guide.showQuickGuide') || 'Show Quick Guide';
            const quickGuideTextSpan = document.createElement('span');
            quickGuideTextSpan.className = 'menu-btn-text';
            quickGuideTextSpan.textContent = quickGuideText;

            quickGuideBtn.appendChild(quickGuideIcon);
            quickGuideBtn.appendChild(quickGuideTextSpan);

            quickGuideBtn.addEventListener('click', async () => {
                closeMenu();
                const { showGuideAgain } = await import('../utils/guide.js');
                showGuideAgain();
            });

            container.appendChild(quickGuideBtn);
        }



        return container;
    }

    async function createInfoLinks() {
        // Check if any footer items should be shown
        const showLogo = MENU_SETTINGS?.LOGO !== false;
        const showMapData = MENU_SETTINGS?.MAP_DATA !== false;
        const showLegalLinks = MENU_SETTINGS?.LEGAL_LINKS !== false;

        if (!showLogo && !showMapData && !showLegalLinks) return null;

        // Main footer container
        const footerContainer = document.createElement('div');
        footerContainer.className = 'menu-footer';

        // 1. Logo
        if (showLogo) {
            // Prefer WebP logos; support legacy PNG paths by normalizing to base
            const base = IMAGE_PATHS?.LOGO
                ? IMAGE_PATHS.LOGO.replace('logo.png', '').replace('logo.webp', '')
                : '/images/logo/';
            const logoImg = document.createElement('img');
            logoImg.src = addVersionToUrl(`${base}logo_full.webp`);
            logoImg.alt = 'Logo';
            logoImg.className = 'menu-attribution-logo';
            footerContainer.appendChild(logoImg);
        }

        // 2. Map Attribution Text
        if (showMapData) {
            const attribution = await createMenuAttributionText();
            if (attribution) {
                footerContainer.appendChild(attribution);
            }
        }

        // 3. Legal Links (two rows with dot separators)
        if (showLegalLinks) {
            const linksContainer = document.createElement('div');
            linksContainer.className = 'menu-links-container';

            // Row 1: Privacy · Terms · Cookies
            const row1 = [
                { id: 'PRIVACY', text: await getText('menu.privacy') || 'Privacy', action: () => showPrivacyPage(getCurrentLanguage()) },
                { id: 'TERMS', text: await getText('menu.terms') || 'Terms', action: () => showPolicyPage(getCurrentLanguage()) },
                { id: 'COOKIES', text: await getText('menu.cookies') || 'Cookies', action: () => showCookiesPage(getCurrentLanguage()) }
            ].filter(link => MENU_SETTINGS?.[link.id] !== false);

            // Row 2: Transparency · Licenses
            const row2 = [
                { id: 'TRANSPARENCY', text: await getText('menu.transparency') || 'Transparency', action: () => showTransparencyPage(getCurrentLanguage()) },
                { id: 'LICENSES', text: await getText('menu.licenses') || 'Licenses', action: () => showLicensesPage(getCurrentLanguage()) }
            ].filter(link => MENU_SETTINGS?.[link.id] !== false);

            const createRow = (links) => {
                const rowDiv = document.createElement('div');
                rowDiv.className = 'menu-links-row';

                links.forEach((link, index) => {
                    const linkElement = document.createElement('button');
                    linkElement.className = 'menu-info-link';
                    linkElement.textContent = link.text;
                    linkElement.addEventListener('click', () => {
                        link.action();
                        closeMenu();
                    });
                    rowDiv.appendChild(linkElement);

                    // Add dot separator except after last item
                    if (index < links.length - 1) {
                        const dot = document.createElement('span');
                        dot.className = 'menu-link-separator';
                        dot.textContent = '·';
                        rowDiv.appendChild(dot);
                    }
                });

                return rowDiv;
            };

            if (row1.length > 0) linksContainer.appendChild(createRow(row1));
            if (row2.length > 0) linksContainer.appendChild(createRow(row2));

            if (linksContainer.children.length > 0) {
                footerContainer.appendChild(linksContainer);
            }
        }

        return footerContainer;
    }

    async function openMenu() {
        log('Opening menu...');
        if (getUIState('popupOpen')) {
            // If a popup is open, close it first
            window.currentPopupInstance?.close();
        }
        if (isMenuOpen) {
            log('Menu already open, skipping.');
            return;
        }
        isMenuOpen = true;
        menuOverlay = acquireOverlay();
        // Ensure overlay click closes the menu
        overlayClickHandler = () => closeMenu();
        addOverlayListener(overlayClickHandler);
        menuLeft.classList.add('open');
        // Add active class to menu toggle button
        if (menuToggle) menuToggle.classList.add('active');
        document.body.style.overflow = 'hidden';

        // Add close button if not present
        if (!closeBtn) {
            closeBtn = createCloseBtn();
            menuLeft.insertBefore(closeBtn, menuLeft.firstChild);
        }
        closeBtn.style.display = '';

        // Add language switcher if not present (and enabled)
        const showLanguageSelector = MENU_SETTINGS?.LANGUAGE_SELECTOR !== false;
        if (showLanguageSelector && !menuLeft.querySelector('.language-switcher')) {
            const langSwitcher = createLanguageSwitcher();
            if (langSwitcher) {
                // Insert after close button
                if (closeBtn.nextSibling) {
                    menuLeft.insertBefore(langSwitcher, closeBtn.nextSibling);
                } else {
                    menuLeft.appendChild(langSwitcher);
                }
            }
        }

        // Update or create area switcher (below language switcher)
        // Always update it to reflect current area
        const existingAreaSwitcher = menuLeft.querySelector('.menu-area-switcher');
        if (existingAreaSwitcher) {
            existingAreaSwitcher.remove();
        }

        const areaSwitcher = await createAreaSwitcher();
        if (areaSwitcher) {
            // Insert after language switcher
            const langSwitcher = menuLeft.querySelector('.language-switcher');
            if (langSwitcher) {
                // Insert after language switcher
                if (langSwitcher.nextSibling) {
                    menuLeft.insertBefore(areaSwitcher, langSwitcher.nextSibling);
                } else {
                    menuLeft.appendChild(areaSwitcher);
                }
            } else {
                // If no language switcher, insert after close button
                if (closeBtn && closeBtn.nextSibling) {
                    menuLeft.insertBefore(areaSwitcher, closeBtn.nextSibling);
                } else {
                    menuLeft.appendChild(areaSwitcher);
                }
            }
        }

        // Update or create favorites section (below area switcher)
        const showFavorites = MENU_SETTINGS?.FAVORITES !== false;
        const existingFavoritesSection = menuLeft.querySelector('.menu-btn-section');
        if (existingFavoritesSection) {
            existingFavoritesSection.remove();
        }

        // Track last inserted element for proper ordering
        let lastInsertedElement = menuLeft.querySelector('.menu-area-switcher')
            || menuLeft.querySelector('.language-switcher')
            || closeBtn;

        if (showFavorites) {
            const favoritesSection = await createFavoritesSection();
            if (favoritesSection) {
                // Insert after last element
                if (lastInsertedElement && lastInsertedElement.nextSibling) {
                    menuLeft.insertBefore(favoritesSection, lastInsertedElement.nextSibling);
                } else if (lastInsertedElement) {
                    menuLeft.appendChild(favoritesSection);
                } else {
                    menuLeft.appendChild(favoritesSection);
                }
                lastInsertedElement = favoritesSection;

                // Initialize favorites button functionality AFTER button is in DOM
                const mapModule = await import('../map/index.js');
                initFavoritesButton({
                    buttonId: 'menu-favorites-btn',
                    mapApi: {
                        showFavorites: mapModule.showFavorites,
                        showAllSpots: mapModule.showAllSpots,
                        getSpots: mapModule.getSpots
                    }
                });
            }
        }

        // Update or create info buttons section (right after favorites, with separator)
        const existingInfoButtons = menuLeft.querySelector('.menu-info-buttons');
        if (existingInfoButtons) {
            existingInfoButtons.remove();
        }
        const existingSeparator = menuLeft.querySelector('.menu-separator');
        if (existingSeparator) {
            existingSeparator.remove();
        }

        const infoButtons = await createInfoButtons();
        if (infoButtons) {
            // Add separator line before info buttons
            const separator = document.createElement('div');
            separator.className = 'menu-separator';

            if (lastInsertedElement && lastInsertedElement.nextSibling) {
                menuLeft.insertBefore(separator, lastInsertedElement.nextSibling);
                menuLeft.insertBefore(infoButtons, separator.nextSibling);
            } else if (lastInsertedElement) {
                menuLeft.appendChild(separator);
                menuLeft.appendChild(infoButtons);
            } else {
                menuLeft.appendChild(separator);
                menuLeft.appendChild(infoButtons);
            }
        }

        // Add info links at the bottom if not present
        if (!menuLeft.querySelector('.menu-footer')) {
            const infoLinks = await createInfoLinks();
            // Check again to prevent race condition if menu was closed/opened rapidly
            if (infoLinks && !menuLeft.querySelector('.menu-footer')) {
                menuLeft.appendChild(infoLinks);
            }
        }
    }

    function closeMenu() {
        log('Closing menu...');
        if (!isMenuOpen) {
            log('Menu not open, skipping close.');
            return;
        }
        isMenuOpen = false;
        setUIState('menuOpen', false);
        menuLeft.classList.remove('open');
        // Remove active class from menu toggle button
        if (menuToggle) menuToggle.classList.remove('active');
        if (overlayClickHandler) {
            removeOverlayListener(overlayClickHandler);
            overlayClickHandler = null;
        }
        if (menuOverlay) {
            releaseOverlay();
            menuOverlay = null;
        }
        document.body.style.overflow = '';
        if (closeBtn) closeBtn.style.display = 'none';

        // Restore header button state based on current URL when menu closes
        restoreActiveFlowFromUrl();
    }

    // Toggle menu on button click
    // Safety check: menuToggle might not exist if initializeHeader hasn't created it yet
    if (!menuToggle) {
        console.warn('[LeftMenu] Menu toggle button not found. Menu functionality will not work.');
        return;
    }

    menuToggle.addEventListener('click', async (e) => {
        // Listen for global popupOpen state changes
        window.addEventListener('uiStateChanged', (e) => {
            if (e.detail.key === 'popupOpen' && e.detail.value === true && isMenuOpen) {
                closeMenu();
            }
        });
        log('Menu toggle clicked');
        // Prevent propagation to avoid immediate closing if something else listens
        e.stopPropagation();

        log('[LeftMenu] Calling clearAllUI({ keepMenu: true, keepSpot: true })');
        clearAllUI({ keepMenu: true, keepSpot: true });
        log('[LeftMenu] clearAllUI complete');
        if (isMenuOpen) {
            closeMenu();
        } else {
            await openMenu();
        }
    });

    // Close menu on escape key
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && isMenuOpen) {
            closeMenu();
        }
    });

    // Update area switcher when area changes (even if menu is open)
    window.addEventListener('areaChanged', async () => {
        if (isMenuOpen && menuLeft) {
            const existingAreaSwitcher = menuLeft.querySelector('.menu-area-switcher');
            if (existingAreaSwitcher) {
                existingAreaSwitcher.remove();
            }

            const areaSwitcher = await createAreaSwitcher();
            if (areaSwitcher) {
                // Insert after language switcher
                const langSwitcher = menuLeft.querySelector('.language-switcher');
                if (langSwitcher) {
                    if (langSwitcher.nextSibling) {
                        menuLeft.insertBefore(areaSwitcher, langSwitcher.nextSibling);
                    } else {
                        menuLeft.appendChild(areaSwitcher);
                    }
                } else {
                    if (closeBtn && closeBtn.nextSibling) {
                        menuLeft.insertBefore(areaSwitcher, closeBtn.nextSibling);
                    } else {
                        menuLeft.appendChild(areaSwitcher);
                    }
                }
            }
        }
    });

    // Listen for external request to close menu (e.g. from clearAllUI)
    window.addEventListener('requestCloseMenu', () => {
        if (isMenuOpen) {
            log('Received requestCloseMenu event, closing...');
            closeMenu();
        }
    });
} 